/**
 * API Client - Centralized HTTP client for all API requests
 *
 * Provides:
 * - Consistent error handling and response typing
 * - Automatic timeout handling
 * - Offline detection
 * - Retry logic for transient failures
 *
 * Single Responsibility: HTTP communication only
 */

import { API_TIMEOUTS, RETRY_CONFIG, AI_RETRY_CONFIG } from '../constants/api'

/**
 * Retry configuration options
 */
interface RetryConfig {
  maxRetries: number
  baseDelayMs: number
  maxDelayMs: number
  retryableStatuses: readonly number[]
}

/**
 * API response wrapper with success/error typing
 */
export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: string; isOffline?: boolean; isTimeout?: boolean }

/**
 * Configuration for API requests
 */
interface RequestConfig {
  timeout?: number
  /** Whether to automatically parse JSON response */
  parseJson?: boolean
}

/**
 * Check if device is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateBackoff(
  attempt: number,
  baseDelay: number,
  maxDelay: number
): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt)
  const jitter = Math.random() * 0.3 * exponentialDelay // 30% jitter
  return Math.min(exponentialDelay + jitter, maxDelay)
}

/**
 * Check if an HTTP status code is retryable
 */
function isRetryableStatus(status: number, retryableStatuses: readonly number[]): boolean {
  return retryableStatuses.includes(status)
}

/**
 * Make a fetch request with timeout
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    })
    return response
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Core request function with error handling
 */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  body?: unknown,
  config: RequestConfig = {}
): Promise<ApiResult<T>> {
  const { timeout = API_TIMEOUTS.default, parseJson = true } = config

  // Check offline status first
  if (!isOnline()) {
    return {
      success: false,
      error: 'You appear to be offline',
      isOffline: true,
    }
  }

  try {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    }

    if (body !== undefined) {
      options.body = JSON.stringify(body)
    }

    const response = await fetchWithTimeout(url, options, timeout)

    if (!response.ok) {
      // Extract error message from response if available
      let errorMessage = `Request failed with status ${response.status}`
      try {
        const errorData = await response.json()
        if (errorData.error) {
          errorMessage = errorData.error
        }
      } catch {
        // Ignore JSON parse errors for error responses
      }

      return { success: false, error: errorMessage }
    }

    if (parseJson) {
      const data = await response.json()
      return { success: true, data: data as T }
    }

    return { success: true, data: null as T }
  } catch (error) {
    if (isTimeoutError(error)) {
      return {
        success: false,
        error: 'Request timed out. Please try again.',
        isTimeout: true,
      }
    }

    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
    return { success: false, error: errorMessage }
  }
}

/**
 * Request with automatic retry and exponential backoff
 */
async function requestWithRetry<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  body?: unknown,
  config: RequestConfig = {},
  retryConfig: RetryConfig = RETRY_CONFIG
): Promise<ApiResult<T>> {
  let lastResult: ApiResult<T> | null = null
  let lastStatus: number | null = null

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    // Check offline before each attempt
    if (!isOnline()) {
      return {
        success: false,
        error: 'You appear to be offline',
        isOffline: true,
      }
    }

    const result = await request<T>(method, url, body, config)

    if (result.success) {
      return result
    }

    lastResult = result

    // Extract status code from error message if present
    const statusMatch = result.error.match(/status (\d+)/)
    lastStatus = statusMatch ? parseInt(statusMatch[1], 10) : null

    // Check if we should retry
    const isRetryable =
      result.isTimeout ||
      (lastStatus !== null && isRetryableStatus(lastStatus, retryConfig.retryableStatuses))

    // Don't retry if not retryable or we've exhausted retries
    if (!isRetryable || attempt === retryConfig.maxRetries) {
      return result
    }

    // Calculate backoff delay and wait
    const delay = calculateBackoff(
      attempt,
      retryConfig.baseDelayMs,
      retryConfig.maxDelayMs
    )
    await sleep(delay)
  }

  // Should never reach here, but TypeScript needs this
  return lastResult!
}

/**
 * API Client with typed methods for each HTTP verb
 */
export const apiClient = {
  /**
   * GET request
   */
  get<T>(url: string, config?: RequestConfig): Promise<ApiResult<T>> {
    return request<T>('GET', url, undefined, config)
  },

  /**
   * POST request
   */
  post<T>(url: string, body?: unknown, config?: RequestConfig): Promise<ApiResult<T>> {
    return request<T>('POST', url, body, config)
  },

  /**
   * POST request with longer timeout and retry for AI endpoints
   */
  postAI<T>(url: string, body?: unknown): Promise<ApiResult<T>> {
    return requestWithRetry<T>(
      'POST',
      url,
      body,
      { timeout: API_TIMEOUTS.ai },
      AI_RETRY_CONFIG
    )
  },

  /**
   * POST request with custom retry configuration
   */
  postWithRetry<T>(
    url: string,
    body?: unknown,
    config?: RequestConfig,
    retryConfig?: RetryConfig
  ): Promise<ApiResult<T>> {
    return requestWithRetry<T>('POST', url, body, config, retryConfig)
  },

  /**
   * PUT request
   */
  put<T>(url: string, body?: unknown, config?: RequestConfig): Promise<ApiResult<T>> {
    return request<T>('PUT', url, body, config)
  },

  /**
   * DELETE request
   */
  delete<T>(url: string, config?: RequestConfig): Promise<ApiResult<T>> {
    return request<T>('DELETE', url, undefined, config)
  },
}

// Re-export for backwards compatibility with existing code
export { fetchWithTimeout }
